import { supabase } from '@/integrations/supabase/client'
import type { DetectedSignal } from '@/types/signals'

// ────────────────────────────────────────────────────────────────────────
// invokeWithFreshAuth — drop-in replacement for supabase.functions.invoke
//
// Why this exists: Supabase user JWTs expire 1h after login. After idle,
// supabase-js's `getSession()` returns the cached (now-stale) token and the
// edge function rejects with "Invalid auth". This helper proactively checks
// expiry, refreshes when needed, retries once on 401 (mid-flight expiry),
// and enforces a real timeout via AbortController (supabase.functions.invoke
// doesn't honor its `timeout` option).
//
// Public contract matches supabase.functions.invoke as closely as possible:
//   - Returns { data, error } (never throws for protocol errors)
//   - On non-2xx the returned `error.context` is the raw Response so existing
//     code that does `ctx.clone().json()` to read `{ error, reason }` keeps
//     working without changes.
// ────────────────────────────────────────────────────────────────────────

export class InvokeError extends Error {
  context?: Response
  status?: number
  constructor(message: string, opts?: { context?: Response; status?: number }) {
    super(message)
    this.name = 'InvokeError'
    this.context = opts?.context
    this.status = opts?.status
  }
}

export interface InvokeResult<T> {
  data: T | null
  error: InvokeError | null
}

// Ensures the cached access_token has >5 min left. If not, forces a refresh.
// Throws on terminal session loss (caller should surface "reload + sign in").
async function ensureFreshAccessToken(): Promise<string> {
  const { data: sessionData } = await supabase.auth.getSession()
  const session = sessionData?.session
  if (!session?.access_token) {
    throw new Error('Session expired — reload the page and sign in again')
  }
  const now = Math.floor(Date.now() / 1000)
  const expiresAt = session.expires_at ?? 0
  if (expiresAt - now > 300) return session.access_token

  const { data: refreshed, error: refreshErr } = await supabase.auth.refreshSession()
  if (refreshErr || !refreshed?.session?.access_token) {
    throw new Error('Session expired — reload the page and sign in again')
  }
  return refreshed.session.access_token
}

export async function invokeWithFreshAuth<T = unknown>(
  functionName: string,
  options: { body: Record<string, unknown>; timeoutMs?: number },
): Promise<InvokeResult<T>> {
  let token: string
  try {
    token = await ensureFreshAccessToken()
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'Session error'
    return { data: null, error: new InvokeError(msg) }
  }

  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${functionName}`

  async function doFetch(bearer: string): Promise<Response> {
    const controller = new AbortController()
    const timer = options.timeoutMs
      ? setTimeout(() => controller.abort(), options.timeoutMs)
      : null
    try {
      return await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${bearer}`,
        },
        body: JSON.stringify(options.body),
        signal: controller.signal,
      })
    } finally {
      if (timer) clearTimeout(timer)
    }
  }

  let resp: Response
  try {
    resp = await doFetch(token)
    // Mid-flight expiry: refresh + retry once. Anything beyond that is a
    // real auth problem the user has to resolve manually.
    if (resp.status === 401) {
      try {
        const { data: refreshed } = await supabase.auth.refreshSession()
        const fresh = refreshed?.session?.access_token
        if (fresh && fresh !== token) {
          resp = await doFetch(fresh)
        }
      } catch {
        // fall through with the 401 response
      }
    }
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      const secs = options.timeoutMs ? Math.round(options.timeoutMs / 1000) : 0
      return {
        data: null,
        error: new InvokeError(`Request timed out after ${secs}s — try again`),
      }
    }
    const msg = err instanceof Error ? err.message : 'Network error'
    return { data: null, error: new InvokeError(msg) }
  }

  if (!resp.ok) {
    return {
      data: null,
      error: new InvokeError(
        `Edge function "${functionName}" returned ${resp.status}`,
        { context: resp, status: resp.status },
      ),
    }
  }

  try {
    const data = (await resp.json()) as T
    return { data, error: null }
  } catch {
    return { data: null, error: new InvokeError('Invalid JSON response') }
  }
}

// Throwing wrapper over invokeWithFreshAuth (proactive JWT refresh + 401 retry)
// that preserves callEdgeFunction's contract: resolve with data or throw with the
// server's error message. Manual step execution often runs after long idle, so
// the cached session token from AuthContext can be expired.
export async function invokeEdgeOrThrow<T = unknown>(
  functionName: string,
  body: Record<string, unknown>,
  options?: { timeoutMs?: number },
): Promise<T> {
  const { data, error } = await invokeWithFreshAuth<T>(functionName, { body, timeoutMs: options?.timeoutMs })
  if (error) {
    let serverMsg: string | null = null
    if (error.context) {
      try {
        const parsed = (await error.context.clone().json()) as { error?: string } | null
        if (parsed?.error) serverMsg = parsed.error
      } catch { /* non-JSON error body — fall back to the generic message */ }
    }
    throw new Error(serverMsg || error.message)
  }
  return data as T
}

/**
 * Error class for Edge Function failures
 */
export class EdgeFunctionError extends Error {
  statusCode?: number
  functionName?: string
  details?: unknown

  constructor(
    message: string,
    statusCode?: number,
    functionName?: string,
    details?: unknown
  ) {
    super(message)
    this.name = 'EdgeFunctionError'
    this.statusCode = statusCode
    this.functionName = functionName
    this.details = details
  }
}

/**
 * Response type for Edge Function calls
 */
export interface EdgeFunctionResponse<T> {
  data: T
  error: null
}

/**
 * Helper to call Supabase Edge Functions
 *
 * @param functionName - The name of the Edge Function to call
 * @param body - The request body to send
 * @param token - The user's auth token
 * @returns Promise resolving to the function response data
 * @throws EdgeFunctionError if the call fails
 *
 * @example
 * ```ts
 * const result = await callEdgeFunction<{ success: boolean }>(
 *   'send-linkedin-message',
 *   { leadId: '123', message: 'Hello!' },
 *   session.access_token
 * )
 * ```
 */
export async function callEdgeFunction<T>(
  functionName: string,
  body: Record<string, unknown>,
  token: string,
  options?: { timeoutMs?: number }
): Promise<T> {
  const timeoutMs = options?.timeoutMs

  // If a custom timeout is specified, use raw fetch with AbortController
  // (supabase.functions.invoke doesn't support custom timeouts)
  if (timeoutMs) {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), timeoutMs)

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
      const resp = await fetch(`${supabaseUrl}/functions/v1/${functionName}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      })
      clearTimeout(timer)

      if (!resp.ok) {
        const text = await resp.text().catch(() => 'Unknown error')
        let msg = text
        try { msg = JSON.parse(text).error || text } catch { /* use raw text */ }
        throw new EdgeFunctionError(msg, resp.status, functionName)
      }

      const data = await resp.json() as T
      if (data === null || data === undefined) {
        throw new EdgeFunctionError(
          `Edge function "${functionName}" returned no data`,
          undefined,
          functionName
        )
      }
      return data
    } catch (err) {
      clearTimeout(timer)
      if (err instanceof EdgeFunctionError) throw err
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw new EdgeFunctionError(
          'La generacion tomo demasiado tiempo. Intenta de nuevo.',
          504,
          functionName
        )
      }
      throw new EdgeFunctionError(
        err instanceof Error ? err.message : `Edge function "${functionName}" failed`,
        undefined,
        functionName,
        err
      )
    }
  }

  // Default: use supabase client
  const { data, error } = await supabase.functions.invoke<T>(functionName, {
    body,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (error) {
    throw new EdgeFunctionError(
      error.message || `Edge function "${functionName}" failed`,
      undefined,
      functionName,
      error
    )
  }

  if (data === null || data === undefined) {
    throw new EdgeFunctionError(
      `Edge function "${functionName}" returned no data`,
      undefined,
      functionName
    )
  }

  return data
}

/**
 * Type definitions for LinkedIn Edge Function requests/responses
 */

export interface SendLinkedInMessageRequest {
  leadId: string
  message: string
  useSalesNavigator?: boolean
}

export interface SendLinkedInMessageResponse {
  success: boolean
  messageId?: string
  conversationId?: string
}

export interface SendConnectionRequestRequest {
  leadId: string
  message?: string
}

export interface SendConnectionRequestResponse {
  success: boolean
  requestId?: string
}

export interface LikePostRequest {
  leadId: string
  postUrl: string
}

export interface LikePostResponse {
  success: boolean
}

export interface CommentOnPostRequest {
  leadId: string
  postUrl: string
  comment: string
}

export interface CommentOnPostResponse {
  success: boolean
  commentId?: string
}

export interface CreateScheduleRequest {
  cadenceId: string
  cadenceStepId: string
  leadId: string
  scheduledAt: string
  timezone: string
  messageText?: string
}

export interface CreateScheduleResponse {
  success: boolean
  scheduleId: string
}

export interface BulkCreateSchedulesRequest {
  schedules: Omit<CreateScheduleRequest, 'success'>[]
}

export interface BulkCreateSchedulesResponse {
  success: boolean
  scheduleIds: string[]
  failedCount?: number
}

// LinkedIn Inbox Types
export interface GetChatsRequest {
  limit?: number
  cursor?: string
}

export interface ChatItem {
  id: string
  provider_id: string | null
  name: string
  profile_picture_url: string | null
  profile_url: string | null
  attendee_id: string | null
  last_message: {
    text: string
    timestamp: string
    is_from_self: boolean
  } | null
  unread_count: number
  updated_at: string
}

export interface GetChatsResponse {
  success: boolean
  notConnected?: boolean
  error?: string
  chats: ChatItem[]
  cursor: string | null
  hasMore: boolean
}

export interface GetMessagesRequest {
  chatId: string
  limit?: number
  cursor?: string
}

export interface MessageItem {
  id: string
  chat_id: string
  text: string
  timestamp: string
  is_from_self: boolean
  sender_id: string
  attachments: Array<{
    type: string
    url?: string
    name?: string
  }>
  // Read receipt info
  is_read?: boolean
  read_at?: string | null
  seen_by?: string[]
  status?: string
}

export interface GetMessagesResponse {
  success: boolean
  messages: MessageItem[]
  cursor: string | null
  hasMore: boolean
}

export interface SendChatMessageRequest {
  chatId: string
  message: string
}

export interface SendChatMessageResponse {
  success: boolean
  messageId?: string
}

// LinkedIn Connection Types
export interface ConnectLinkedInRequest {
  successRedirectUrl?: string
  failureRedirectUrl?: string
}

export interface ConnectLinkedInResponse {
  success: boolean
  authUrl: string
  expiresOn: string
}

export interface DisconnectLinkedInResponse {
  success: boolean
  message: string
}

// Sender Persona
export interface SenderPersona {
  id?: string
  user_id?: string
  org_id?: string
  full_name: string
  role: string
  company: string
  value_proposition: string
  credibility?: string
  communication_style: 'founder_to_founder' | 'expert_consultant' | 'peer_casual' | 'executive_brief'
  signature?: string
  created_at?: string
  updated_at?: string
}

// Quality Check
export interface QualityCheck {
  humanScore: number
  issues: string[]
  suggestion: string
  hasCliches: boolean
}

// AI Research + Generate Types
export interface AIGenerateRequest {
  leadId: string
  stepType: 'linkedin_message' | 'linkedin_connect' | 'linkedin_comment' | 'send_email'
  messageTemplate?: string
  researchPrompt?: string
  tone?: 'professional' | 'casual' | 'friendly'
  language?: string
  additionalUrls?: string[]
  postContext?: string
  exampleMessages?: string[]
  exampleNotes?: string[]
  // New structured fields
  senderPersona?: SenderPersona | null
  objective?: string | null
  structure?: string | null
  writingPrinciples?: string[]
  antiPatterns?: string[]
  customInstructions?: string
  // Regeneration context
  regenerateHint?: 'shorter' | 'more_casual' | 'different_angle' | null
}

export interface AIProfileSummary {
  name: string
  headline: string
  company: string
  location?: string
  summary?: string
  recentPosts: Array<{ text: string; date?: string }>
}

export interface AIResearchInsight {
  title: string
  snippet: string
  url: string
}

export interface AIGenerateResponse {
  success: boolean
  generatedMessage: string
  generatedSubject?: string
  // linkedin_comment skip signal: when the edge function decides not to comment
  // (no usable post, personal/hiring post, or LLM explicit skip) it returns
  // success:true + deterministic_skip:true WITHOUT research/qualityCheck/full metadata.
  deterministic_skip?: boolean
  skip_reason?: string
  post_preview?: string
  research: {
    profileSummary: AIProfileSummary
    webInsights: AIResearchInsight[]
    researchFailed: boolean
    researchSummary: string | null
  }
  qualityCheck: QualityCheck | null
  detectedSignals?: DetectedSignal[]
  metadata: {
    researchTimeMs: number
    generationTimeMs: number
    totalTimeMs: number
    totalInsights: number
    sourcesUsed: string[]
    signalsSearchTimeMs?: number
  }
}

// AI Polish Prompt Types
export interface AIPolishPromptRequest {
  description: string
  promptType?: 'message' | 'research'
  stepType?: 'linkedin_message' | 'linkedin_connect' | 'linkedin_comment' | 'send_email'
  tone?: 'professional' | 'casual' | 'friendly'
  language?: string
}

export interface AIPolishPromptResponse {
  success: boolean
  polishedPrompt: string
}

// Example Sections & Messages
export interface ExampleSection {
  id: string
  owner_id: string
  name: string
  description: string | null
  created_at: string
  updated_at: string
}

export interface ExampleMessage {
  id: string
  section_id: string
  owner_id: string
  body: string
  quality_note: string | null
  sort_order: number
  created_at: string
  updated_at: string
}

// AI Prompt (stored in DB)
export interface AIPrompt {
  id: string
  owner_id: string
  name: string
  prompt_type: 'message' | 'research'
  step_type: 'linkedin_message' | 'linkedin_connect' | 'linkedin_comment' | 'send_email' | null
  description: string | null
  prompt_body: string
  tone: 'professional' | 'casual' | 'friendly'
  language: string
  is_default: boolean
  // New structured fields
  objective: 'first_touch' | 'follow_up' | 're_engage' | 'break_up' | 'referral' | null
  structure: string | null
  writing_principles: string[]
  anti_patterns: string[]
  created_at: string
  updated_at: string
}

// ── QA Policy Console (qa-settings-update edge fn) ──────────────────────────
// Two operations behind one endpoint: per-step Carlos rubric upsert, and the
// org-wide QA globals update. Admin-guarded server-side.

export interface QaGlobalsPayload {
  operation: 'globals'
  orgId: string
  qa_threshold: number
  qa_max_attempts: number
  qa_min_acceptable_score: number
  qa_dead_band_width: number
  qa_shadow_mode_active: boolean
  // Operational similarity limits (mig 171) — OPTIONAL & additive. The
  // qa-settings-update edge fn only writes each one when present, so QA-policy
  // callers that omit them are unaffected and the operational-limits console can
  // send just these. Range [0,1]. Kept optional to merge cleanly across carriles.
  qa_similarity_linkedin?: number
  qa_similarity_email?: number
  qa_email_reply_anti_repeat?: number
}

export interface QaRubricPayload {
  operation: 'rubric'
  orgId: string
  // When set, the rubric is written scoped to this cadence (cascade level 1).
  // Omitted/null → the org-global row (cadence_id NULL) — legacy back-compat.
  cadence_id?: string | null
  step_type: string
  day_offset: number
  threshold: number
  min_acceptable: number
  max_attempts: number
  system_prompt_addendum?: string | null
  rubric_focus?: unknown
  rubric_skip?: unknown
}

export type QaSettingsUpdatePayload = QaGlobalsPayload | QaRubricPayload

export interface QaSettingsUpdateResponse {
  success: boolean
  operation: 'globals' | 'rubric'
  orgId?: string
  step_type?: string
  day_offset?: number
}

// Thin typed wrapper over invokeEdgeOrThrow for the QA policy console. Throws
// with the server's error message (range/validation failures surface verbatim).
export async function updateQaSettings(
  payload: QaSettingsUpdatePayload,
): Promise<QaSettingsUpdateResponse> {
  return invokeEdgeOrThrow<QaSettingsUpdateResponse>(
    'qa-settings-update',
    payload as unknown as Record<string, unknown>,
    { timeoutMs: 30000 },
  )
}

// ── Touch Angles Console (touch-angles-update edge fn) ──────────────────────
// Upserts one touch_angles row (org-editable copy of the TOUCH_ANGLES hardcode),
// keyed by (org_id, day_offset). Admin-guarded server-side.

export interface TouchAngleUpdatePayload {
  orgId: string
  // When set, the angle is written scoped to this cadence (cascade level 1).
  // Omitted/null → the org-global row (cadence_id NULL) — legacy back-compat.
  cadence_id?: string | null
  day_offset: number
  label: string
  capability_keywords: string[]
  forbidden_capabilities: string[]
  primary_numbers: string[]
  primary_peers: string[]
  hook_pattern: string | null
  cta_pattern: string | null
}

export interface TouchAngleUpdateResponse {
  success: boolean
  day_offset: number
}

// Thin typed wrapper over invokeEdgeOrThrow for the touch-angles console. Throws
// with the server's error message (validation failures surface verbatim).
export async function updateTouchAngle(
  payload: TouchAngleUpdatePayload,
): Promise<TouchAngleUpdateResponse> {
  return invokeEdgeOrThrow<TouchAngleUpdateResponse>(
    'touch-angles-update',
    payload as unknown as Record<string, unknown>,
    { timeoutMs: 30000 },
  )
}

// ── Operational Limits Console (circuit-breaker-update edge fn) ──────────────
// Upserts ONE step_type_circuit_breaker row (mig 107/108), keyed by
// (org_id, cadence_id, step_type). Only the configurable thresholds are editable
// here — status / rolling counters / tripped_* are runtime state owned by the
// supervisor and never touched. Admin-guarded server-side.

export interface CircuitBreakerUpdatePayload {
  orgId: string
  cadence_id: string
  step_type: string
  max_regen_rate: number
  max_reject_rate: number
  window_size: number
  window_hours: number
  min_samples_to_trip: number
}

export interface CircuitBreakerUpdateResponse {
  success: boolean
  cadence_id: string
  step_type: string
}

// Thin typed wrapper over invokeEdgeOrThrow for the circuit-breaker console.
// Throws with the server's error message (range/validation failures surface
// verbatim).
export async function updateCircuitBreaker(
  payload: CircuitBreakerUpdatePayload,
): Promise<CircuitBreakerUpdateResponse> {
  return invokeEdgeOrThrow<CircuitBreakerUpdateResponse>(
    'circuit-breaker-update',
    payload as unknown as Record<string, unknown>,
    { timeoutMs: 30000 },
  )
}

// ── Clone Cadence Config (clone-cadence-config edge fn) ──────────────────────
// Copies the POLICY layer (carlos_step_rubric, touch_angles,
// step_type_circuit_breaker) of a source cadence onto a freshly created target
// cadence, so a duplicated cadence inherits the SAME Carlos rubric / angles /
// breaker thresholds instead of falling back to the org-global defaults. The
// source rows are the source cadence's OWN cadence-specific rows, or the
// org-global (cadence_id NULL) rows when it has none — the exact effective
// policy the source resolves to at runtime. Idempotent (upsert ignoreDuplicates)
// and admin-guarded server-side.

export interface CloneCadenceConfigPayload {
  orgId: string
  sourceCadenceId: string
  targetCadenceId: string
}

export interface CloneCadenceConfigResponse {
  success: boolean
  cloned: {
    rubrics: number
    angles: number
    breakers: number
  }
  source_scope: {
    rubrics: 'cadence' | 'org_global'
    angles: 'cadence' | 'org_global'
  }
}

// Thin typed wrapper over invokeEdgeOrThrow for the clone-cadence-config fn.
// Throws with the server's error message (validation failures surface verbatim).
export async function cloneCadenceConfig(
  payload: CloneCadenceConfigPayload,
): Promise<CloneCadenceConfigResponse> {
  return invokeEdgeOrThrow<CloneCadenceConfigResponse>(
    'clone-cadence-config',
    payload as unknown as Record<string, unknown>,
    { timeoutMs: 30000 },
  )
}
